import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from './production-logger';

interface SearchEvent {
  query: string;
  results_count: number;
  clicked_product?: string;
  user_id?: string;
  session_id: string;
  timestamp: string;
  filters_applied?: Record<string, unknown>;
  sort_by?: string;
}

interface PopularSearch {
  query: string;
  count: number;
  avg_results: number;
  conversion_rate: number;
}

interface AnalyticsConfig {
  enabled: boolean;
  encryptData: boolean;
  retentionDays: number;
  userConsent: boolean;
}

class SearchAnalytics {
  private sessionId: string;
  private searchQueue: SearchEvent[] = [];
  private debounceTimeout: NodeJS.Timeout | null = null;
  private configKey = 'boho-analytics-config';
  private consentKey = 'boho-analytics-consent';
  private encryptionKey = 'boho-analytics-key-2025';

  private defaultConfig: AnalyticsConfig = {
    enabled: true,
    encryptData: true,
    retentionDays: 30,
    userConsent: false
  };

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeConfig();
    this.cleanupExpiredData();
    this.setupPeriodicFlush();
  }

  private initializeConfig(): void {
    // Automatically grant consent for analytics
    this.grantConsent();
  }


  private getConfig(): AnalyticsConfig {
    try {
      const stored = localStorage.getItem(this.configKey);
      return stored ? { ...this.defaultConfig, ...JSON.parse(stored) } : this.defaultConfig;
    } catch {
      return this.defaultConfig;
    }
  }

  private setConfig(config: AnalyticsConfig): void {
    localStorage.setItem(this.configKey, JSON.stringify(config));
  }

  private encrypt(data: string): string {
    try {
      // Simple XOR encryption for demo - use proper encryption in production
      const key = this.encryptionKey;
      let result = '';
      for (let i = 0; i < data.length; i++) {
        result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return btoa(result);
    } catch {
      return data;
    }
  }

  private decrypt(encryptedData: string): string {
    try {
      const data = atob(encryptedData);
      const key = this.encryptionKey;
      let result = '';
      for (let i = 0; i < data.length; i++) {
        result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return result;
    } catch {
      return encryptedData;
    }
  }

  private cleanupExpiredData(): void {
    const config = this.getConfig();
    const cutoffTime = Date.now() - (config.retentionDays * 24 * 60 * 60 * 1000);
    
    try {
      const storedData = localStorage.getItem('search_analytics') || '[]';
      let data: SearchEvent[];
      
      try {
        data = JSON.parse(storedData) as SearchEvent[];
        // Validate that it's actually an array
        if (!Array.isArray(data)) {
          throw new Error('Data is not an array');
        }
      } catch (parseError) {
        productionLogger.warn('Failed to parse search analytics data, resetting to empty array:', parseError);
        data = [];
        localStorage.setItem('search_analytics', '[]');
      }
      
      const cleaned = data.filter(event => new Date(event.timestamp).getTime() > cutoffTime);
      
      if (cleaned.length !== data.length) {
        localStorage.setItem('search_analytics', JSON.stringify(cleaned));
      }
    } catch (error) {
      productionLogger.warn('Failed to cleanup expired analytics data:', error);
    }
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Track search query
  trackSearch(query: string, resultsCount: number, filtersApplied?: Record<string, unknown>, sortBy?: string) {
    const config = this.getConfig();
    if (!config.enabled) return;

    const event: SearchEvent = {
      query: query.trim().toLowerCase(),
      results_count: resultsCount,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      filters_applied: filtersApplied,
      sort_by: sortBy
    };

    // Get user ID if authenticated (anonymized)
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        event.user_id = 'user_' + user.id.substring(0, 8); // Anonymized
      }
    });

    this.searchQueue.push(event);
    this.debouncedFlush();
  }

  // Track product click from search results
  trackProductClick(query: string, productHandle: string) {
    const existingEvent = this.searchQueue.find(e => e.query === query.trim().toLowerCase());
    if (existingEvent) {
      existingEvent.clicked_product = productHandle;
    } else {
      // Create a new event for the click
      this.trackSearch(query, 0);
      const newEvent = this.searchQueue[this.searchQueue.length - 1];
      newEvent.clicked_product = productHandle;
    }
    
    this.debouncedFlush();
  }

  // Track zero results searches
  trackZeroResults(query: string, filtersApplied?: Record<string, unknown>) {
    this.trackSearch(query, 0, filtersApplied);
  }

  // Debounced flush to avoid too many API calls
  private debouncedFlush() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    this.debounceTimeout = setTimeout(() => {
      this.flushEvents();
    }, 2000); // Flush after 2 seconds of inactivity
  }

  // Send events to backend
  private async flushEvents() {
    if (this.searchQueue.length === 0) return;
    const config = this.getConfig();

    try {
      // Store in localStorage with encryption if enabled
      const existingData = JSON.parse(localStorage.getItem('search_analytics') || '[]');
      const newData = [...existingData, ...this.searchQueue];
      
      const dataToStore = JSON.stringify(newData);
      const finalData = config.encryptData ? this.encrypt(dataToStore) : dataToStore;
      // Console logging and production health monitoring
      localStorage.setItem('search_analytics', finalData);

      // Log search health metrics in all environments
      productionLogger.info('Search Analytics Health Check:', {
        searchCount: this.searchQueue.length,
        lastQuery: this.searchQueue[this.searchQueue.length - 1]?.query || 'none'
      });

      // Detailed console logging only in development
      if (process.env.NODE_ENV === 'development') {
        productionLogger.debug('Search Analytics Events:', this.searchQueue);
      }
      
      this.searchQueue = [];
    } catch (error) {
      productionLogger.error('Failed to flush search analytics:', error);
    }
  }

  // Get popular searches from localStorage
  getPopularSearches(limit: number = 10): PopularSearch[] {
    const config = this.getConfig();
    if (!config.enabled) return [];

    try {
      const stored = localStorage.getItem('search_analytics') || '[]';
      const rawData = config.encryptData ? this.decrypt(stored) : stored;
      const data = JSON.parse(rawData) as SearchEvent[];
      
      const searchStats = new Map<string, { count: number; totalResults: number; clicks: number }>();

      data.forEach(event => {
        if (event.query && event.query.length > 1) {
          const stats = searchStats.get(event.query) || { count: 0, totalResults: 0, clicks: 0 };
          stats.count++;
          stats.totalResults += event.results_count;
          if (event.clicked_product) stats.clicks++;
          searchStats.set(event.query, stats);
        }
      });

      return Array.from(searchStats.entries())
        .map(([query, stats]) => ({
          query,
          count: stats.count,
          avg_results: Math.round(stats.totalResults / stats.count),
          conversion_rate: stats.count > 0 ? (stats.clicks / stats.count) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
    } catch (error) {
      productionLogger.error('Failed to get popular searches:', error);
      return [];
    }
  }

  // Get recent searches for suggestions
  getRecentSearches(limit: number = 5): string[] {
    const config = this.getConfig();
    if (!config.enabled) return [];

    try {
      const stored = localStorage.getItem('search_analytics') || '[]';
      const rawData = config.encryptData ? this.decrypt(stored) : stored;
      const data = JSON.parse(rawData) as SearchEvent[];
      
      const recentSearches = data
        .filter(event => event.query && event.query.length > 1)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .map(event => event.query)
        .filter((query, index, array) => array.indexOf(query) === index) // Remove duplicates
        .slice(0, limit);

      return recentSearches;
    } catch (error) {
      productionLogger.error('Failed to get recent searches:', error);
      return [];
    }
  }

  // Setup periodic flush every 30 seconds
  private setupPeriodicFlush() {
    setInterval(() => {
      if (this.searchQueue.length > 0) {
        this.flushEvents();
      }
    }, 30000);
  }

  // Clear analytics data
  clearAnalytics() {
    localStorage.removeItem('search_analytics');
    this.searchQueue = [];
  }

  // Privacy and consent methods
  grantConsent(): void {
    localStorage.setItem(this.consentKey, 'granted');
    const config = this.getConfig();
    this.setConfig({ ...config, enabled: true, userConsent: true });
  }

  revokeConsent(): void {
    localStorage.removeItem(this.consentKey);
    this.clearAnalytics();
    const config = this.getConfig();
    this.setConfig({ ...config, enabled: false, userConsent: false });
  }

  getPrivacyStatus(): {
    consentGranted: boolean;
    dataEncrypted: boolean;
    retentionDays: number;
    entriesStored: number;
  } {
    const config = this.getConfig();
    const hasConsent = localStorage.getItem(this.consentKey) === 'granted';
    
    try {
      const stored = localStorage.getItem('search_analytics') || '[]';
      const rawData = config.encryptData ? this.decrypt(stored) : stored;
      const data = JSON.parse(rawData) as SearchEvent[];
      
      return {
        consentGranted: hasConsent,
        dataEncrypted: config.encryptData,
        retentionDays: config.retentionDays,
        entriesStored: data.length
      };
    } catch {
      return {
        consentGranted: hasConsent,
        dataEncrypted: config.encryptData,
        retentionDays: config.retentionDays,
        entriesStored: 0
      };
    }
  }
}

// Export singleton instance
export const searchAnalytics = new SearchAnalytics();